import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AdminDashboardPayload,
  Document,
  Flat,
  InitiateRentReviewPayload,
  Invoice,
  Notice,
  NoticeRecipient,
  Paginated,
  Payment,
  RentalApplication,
  RentReview,
  RenewTenancyPayload,
  ReversePaymentPayload,
  SingleResource,
  StoreFlatPayload,
  StoreNoticePayload,
  StoreInvoicePayload,
  StorePaymentPayload,
  Tenancy,
  Tenant,
  TerminateTenancyPayload,
  UpdateNoticePayload,
} from "./types";

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboardQuery() {
  return useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => {
      const { data } = await api.get<AdminDashboardPayload>("/api/admin/dashboard");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Notifications ──────────────────────────────────────────────────────────
// There's no general notification subsystem — this surfaces the two real
// admin-actionable signals that exist today (IOBOTech sync conflicts, the
// pending tenant-approval queue). See NotificationsController for why
// Notice/NoticeRecipient can't back this (tenant-only, not admin-facing).

export interface AdminNotificationItem {
  type: "sync_conflict" | "pending_approvals" | "unconfirmed_recharges";
  title: string;
  detail: string | null;
  timestamp: string | null;
  link: string;
}

export function useAdminNotificationsQuery() {
  return useQuery({
    queryKey: ["admin", "notifications"],
    queryFn: async () => {
      const { data } = await api.get<{ unread_count: number; items: AdminNotificationItem[] }>(
        "/api/admin/notifications",
      );
      return data;
    },
    refetchInterval: 60_000,
  });
}

// ─── Tenants ────────────────────────────────────────────────────────────────

export interface TenantsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  has_active_tenancy?: boolean;
  sort?: string;
}

export function useTenantsQuery(params: TenantsQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "tenants", params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Tenant>>("/api/admin/tenants", {
        params: tenantParamsToQuery(params),
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

function tenantParamsToQuery(p: TenantsQueryParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (p.page) q["page"] = p.page;
  if (p.per_page) q["per_page"] = p.per_page;
  if (p.sort) q["sort"] = p.sort;
  if (p.search) q["filter[search]"] = p.search;
  if (p.has_active_tenancy !== undefined) {
    q["filter[has_active_tenancy]"] = p.has_active_tenancy ? 1 : 0;
  }
  return q;
}

export function useTenantQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "tenants", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Tenant>>(`/api/admin/tenants/${id}`);
      return data.data;
    },
  });
}

// Acknowledges a flagged IOBOTech unitId mismatch once the admin has manually
// verified/fixed it — only clears the flag, doesn't itself change any linkage.
export function useResolveSyncConflictMutation(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SingleResource<Tenant>>(
        `/api/admin/tenants/${tenantId}/resolve-sync-conflict`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", tenantId] });
    },
  });
}

// Reassigns which flat a tenant is linked to — the actual fix for a flagged
// sync conflict (also clears the conflict note server-side on success).
// Rejected with a 422 if the tenant has an active tenancy.
export function useUpdateTenantFlatMutation(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flatId: string) => {
      const { data } = await api.patch<SingleResource<Tenant>>(
        `/api/admin/tenants/${tenantId}/flat`,
        { flat_id: flatId },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export interface UpdateTenantPayload {
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  address?: string | null;
  notes?: string | null;
}

export function useUpdateTenantMutation(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateTenantPayload) => {
      const { data } = await api.patch<SingleResource<Tenant>>(
        `/api/admin/tenants/${tenantId}`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", tenantId] });
    },
  });
}

export interface TenantIOBOTechData {
  wallet: {
    balance: number | null;
    balance_formatted: string | null;
    account_number: string | null;
    account_name: string | null;
    bank_name: string | null;
  } | null;
  bills: Array<{
    billingId: string;
    name: string;
    description: string;
    amount: number;
    status: string;
    priority: string;
  }>;
  invoices: Array<{
    invoiceId: string;
    billingId: string;
    bills: string;
    unit: string;
    amount: number;
    amountToPay: number;
    isPaid: boolean;
    isDisbursed: boolean;
    paymentMethod: string | null;
    penaltyFee: number;
    status: string;
    dueDate: string | null;
    collectionDate: string | null;
    reference: string | null;
    createdAt: string;
  }>;
  meters: Array<{
    meter_store_id: string | null;
    meter_source_id: string | null;
    meter_number: string | null;
    meter_name: string | null;
    has_remote_access: boolean;
  }>;
  // Real-time, straight from IOBOTech's per-unit invoices endpoint — sees
  // charges `invoices` above structurally cannot (e.g. "installments"-
  // product invoices with no discoverable parent bill in the recurring
  // bill catalog `invoices` is built from). Kept separate from `invoices`
  // deliberately, so the two sources stay distinguishable if they disagree.
  live_unpaid_invoices: Array<{
    invoice_id: string | null;
    name: string | null;
    product: string | null;
    amount: number;
    is_paid: boolean;
    is_due: boolean;
    due_date: string | null;
  }>;
  flat: { code: string; unit: string; block: string } | null;
}

export function useTenantIOBOTechQuery(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["admin", "tenants", id, "iobotech"],
    enabled: !!id && (options?.enabled ?? true),
    queryFn: async () => {
      const { data } = await api.get<TenantIOBOTechData>(`/api/admin/tenants/${id}/iobotech`);
      return data;
    },
  });
}

export interface AdminMeterBalance {
  balance: number | null;
  is_on: boolean | null;
  power: string | null;
  voltage: string | null;
  energy: string | null;
  measurement_unit: string | null;
}

// On-demand only — not every remote-capable meter actually supports this
// call (confirmed: has_remote_access alone doesn't guarantee it).
export function useCheckAdminMeterBalanceMutation(tenantId: string | undefined) {
  return useMutation({
    mutationFn: async (meterNumber: string) => {
      const { data } = await api.get<{ data: AdminMeterBalance }>(
        `/api/admin/tenants/${tenantId}/meter-balance`,
        { params: { meter_number: meterNumber } },
      );
      return data.data;
    },
  });
}

// Real-world consequence — cuts or restores a tenant's actual power. Gated
// server-side behind the tenants.meter_switch permission (super_admin only
// by default); the button that triggers this should be hidden for anyone
// without that permission too, but the server check is what actually matters.
export function useSwitchMeterMutation(tenantId: string | undefined) {
  return useMutation({
    mutationFn: async (payload: { meter_number: string; is_switch_on: boolean }) => {
      const { data } = await api.post(`/api/admin/tenants/${tenantId}/switch-meter`, payload);
      return data;
    },
  });
}

export interface TenantTransactionReceipt {
  id: string;
  receipt_number: string;
  transaction_type: string;
  amount_kobo: number;
  amount_formatted: string;
  payment_method: string | null;
  iobotech_reference: string | null;
  payment_date: string | null;
  created_at: string;
}

export function useTenantTransactionsQuery(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["admin", "tenants", id, "transactions"],
    enabled: !!id && (options?.enabled ?? true),
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantTransactionReceipt[] }>(
        `/api/admin/tenants/${id}/transactions`
      );
      return data.data;
    },
  });
}

// IOBOTech has no wallet/payment transaction history API — these are
// inferred by SnapshotWalletBalances from balance changes, not confirmed
// transactions, so the UI must say "detected" rather than claim certainty.
export interface WalletMovement {
  id: string;
  type: "topup" | "unexplained_debit";
  amount_kobo: number;
  amount_formatted: string;
  explained_debits_kobo: number;
  window_start: string | null;
  window_end: string | null;
}

export interface TenantWalletActivity {
  data: WalletMovement[];
  shared_with: string[];
}

export function useTenantWalletActivityQuery(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["admin", "tenants", id, "wallet-activity"],
    enabled: !!id && (options?.enabled ?? true),
    queryFn: async () => {
      const { data } = await api.get<TenantWalletActivity>(`/api/admin/tenants/${id}/wallet-activity`);
      return data;
    },
  });
}

// ─── Flats ──────────────────────────────────────────────────────────────────

export interface FlatsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: Flat["status"];
  block?: string;
  /** Vacant or maintenance (the Create-Tenancy wizard uses this) */
  available?: boolean;
  sort?: string;
}

export function useFlatsQuery(params: FlatsQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "flats", params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Flat>>("/api/admin/flats", {
        params: flatParamsToQuery(params),
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

function flatParamsToQuery(p: FlatsQueryParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (p.page) q["page"] = p.page;
  if (p.per_page) q["per_page"] = p.per_page;
  if (p.sort) q["sort"] = p.sort;
  if (p.search) q["filter[search]"] = p.search;
  if (p.status) q["filter[status]"] = p.status;
  if (p.block) q["filter[block]"] = p.block;
  if (p.available) q["filter[available]"] = 1;
  return q;
}

// ─── Invoices (detail) ──────────────────────────────────────────────────────

export function useInvoiceQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "invoices", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Invoice>>(`/api/admin/invoices/${id}`);
      return data.data;
    },
  });
}

// ─── Invoices (index) ───────────────────────────────────────────────────────

export interface InvoicesQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: Invoice["status"];
  type?: Invoice["type"];
  overdue?: boolean;
  sort?: string;
}

export function useInvoicesQuery(params: InvoicesQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "invoices", params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Invoice>>("/api/admin/invoices", {
        params: invoiceParamsToQuery(params),
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

function invoiceParamsToQuery(p: InvoicesQueryParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (p.page) q["page"] = p.page;
  if (p.per_page) q["per_page"] = p.per_page;
  if (p.sort) q["sort"] = p.sort;
  if (p.search) q["filter[search]"] = p.search;
  if (p.status) q["filter[status]"] = p.status;
  if (p.type) q["filter[type]"] = p.type;
  if (p.overdue) q["filter[overdue]"] = 1;
  return q;
}

// ─── Tenancies ──────────────────────────────────────────────────────────────

export interface TenanciesQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: Tenancy["status"];
  tenant_id?: string;
  flat_id?: string;
  sort?: string;
  expiring_within_days?: number;
}

export function useTenanciesQuery(params: TenanciesQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "tenancies", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.sort) q["sort"] = params.sort;
      if (params.search) q["filter[search]"] = params.search;
      if (params.status) q["filter[status]"] = params.status;
      if (params.tenant_id) q["filter[tenant_id]"] = params.tenant_id;
      if (params.flat_id) q["filter[flat_id]"] = params.flat_id;
      if (params.expiring_within_days) q["filter[expiring_within_days]"] = params.expiring_within_days;
      const { data } = await api.get<Paginated<Tenancy>>("/api/admin/tenancies", {
        params: q,
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useTenancyQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "tenancies", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Tenancy>>(`/api/admin/tenancies/${id}`);
      return data.data;
    },
  });
}

// ─── Rental Applications (rental booking module) ──────────────────────────

export interface RentalApplicationsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: RentalApplication["status"];
  flat_id?: string;
  sort?: string;
}

export function useRentalApplicationsQuery(params: RentalApplicationsQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "rental-applications", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.sort) q["sort"] = params.sort;
      if (params.search) q["filter[search]"] = params.search;
      if (params.status) q["filter[status]"] = params.status;
      if (params.flat_id) q["filter[flat_id]"] = params.flat_id;
      const { data } = await api.get<Paginated<RentalApplication>>("/api/admin/rental-applications", {
        params: q,
      });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useRentalApplicationQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "rental-applications", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<RentalApplication>>(`/api/admin/rental-applications/${id}`);
      return data.data;
    },
  });
}

export interface RecordKycPayload {
  government_id_type: string;
  government_id_number: string;
  date_of_birth: string;
  gender: string;
  nationality: string;
  notes?: string;
}

export function useRecordKycMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rentalApplicationId, ...payload }: RecordKycPayload & { rentalApplicationId: string }) => {
      const { data } = await api.post<{ id: string; status: string; submitted_at: string; flat_reclaimed: boolean }>(
        `/api/admin/rental-applications/${rentalApplicationId}/kyc`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "rental-applications", variables.rentalApplicationId] });
    },
  });
}

export function useUploadKycDocumentMutation() {
  return useMutation({
    mutationFn: async ({ kycApplicationId, docType, file }: { kycApplicationId: string; docType: string; file: File }) => {
      const formData = new FormData();
      formData.append("doc_type", docType);
      formData.append("file", file);
      const { data } = await api.post<{ id: string; doc_type: string }>(
        `/api/admin/kyc/applications/${kycApplicationId}/documents`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useUpdateFlatMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<StoreFlatPayload> & { id: string }) => {
      const { data } = await api.patch<SingleResource<Flat>>(`/api/admin/flats/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "flats"] });
    },
  });
}

// Plan §4a/§2.3 stop-gap close-out — the admin action that promotes a
// unit_type-confirmed flat to publicly bookable (rejected 422 otherwise).
export function usePromoteFlatToAvailableMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flatId: string) => {
      const { data } = await api.post<SingleResource<Flat>>(`/api/admin/flats/${flatId}/promote-to-available`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "flats"] });
    },
  });
}

// Listing photo gallery (plan §2.8) — multipart upload, drag-order persist,
// single delete. All three return the updated Flat so the dialog can
// re-render its photo grid from one source of truth.
export function useUploadFlatPhotosMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ flatId, files }: { flatId: string; files: File[] }) => {
      const form = new FormData();
      files.forEach((f) => form.append("photos[]", f));
      const { data } = await api.post<SingleResource<Flat>>(`/api/admin/flats/${flatId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "flats"] }),
  });
}

export function useReorderFlatPhotosMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ flatId, mediaIds }: { flatId: string; mediaIds: string[] }) => {
      const { data } = await api.patch<SingleResource<Flat>>(`/api/admin/flats/${flatId}/photos/reorder`, {
        media_ids: mediaIds,
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "flats"] }),
  });
}

export function useDeleteFlatPhotoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ flatId, mediaId }: { flatId: string; mediaId: string }) => {
      const { data } = await api.delete<SingleResource<Flat>>(`/api/admin/flats/${flatId}/photos/${mediaId}`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "flats"] }),
  });
}

// ─── Feature Groups (plan §2.7) ────────────────────────────────────────────

export interface FeatureItem {
  id: string;
  feature_group_id: string;
  name: string;
  icon: string | null;
  sort_order: number;
}

export interface FeatureGroupItem {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  features: FeatureItem[];
}

export function useFeatureGroupsQuery() {
  return useQuery({
    queryKey: ["admin", "feature-groups"],
    queryFn: async () => {
      const { data } = await api.get<{ data: FeatureGroupItem[] }>("/api/admin/feature-groups");
      return data.data;
    },
  });
}

function invalidateFeatureGroups(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "feature-groups"] });
}

export function useCreateFeatureGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; icon?: string }) => {
      const { data } = await api.post<{ data: FeatureGroupItem }>("/api/admin/feature-groups", payload);
      return data.data;
    },
    onSuccess: () => invalidateFeatureGroups(qc),
  });
}

export function useUpdateFeatureGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; name?: string; icon?: string; sort_order?: number }) => {
      const { data } = await api.patch<{ data: FeatureGroupItem }>(`/api/admin/feature-groups/${id}`, payload);
      return data.data;
    },
    onSuccess: () => invalidateFeatureGroups(qc),
  });
}

export function useDeleteFeatureGroupMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/feature-groups/${id}`);
    },
    onSuccess: () => invalidateFeatureGroups(qc),
  });
}

export function useCreateFeatureMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, ...payload }: { groupId: string; name: string; icon?: string }) => {
      const { data } = await api.post<{ data: FeatureItem }>(`/api/admin/feature-groups/${groupId}/features`, payload);
      return data.data;
    },
    onSuccess: () => invalidateFeatureGroups(qc),
  });
}

export function useDeleteFeatureMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/features/${id}`);
    },
    onSuccess: () => invalidateFeatureGroups(qc),
  });
}

// ─── Co-Tenancy Units ───────────────────────────────────────────────────────
// The physical shared apartment (e.g. "4-Bedroom — Unit 4A") that
// individually-priced, individually-tenanted room-Flats group under for
// public display only — never referenced by booking/tenancy logic.

export interface CoTenancyUnitItem {
  id: string;
  block: string;
  label: string;
  bedroom_count: number;
  shared_kitchen_description: string | null;
  shared_liability_note: string | null;
  rooms_count: number;
}

export function useCoTenancyUnitsQuery() {
  return useQuery({
    queryKey: ["admin", "co-tenancy-units"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CoTenancyUnitItem[] }>("/api/admin/co-tenancy-units");
      return data.data;
    },
  });
}

function invalidateCoTenancyUnits(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "co-tenancy-units"] });
}

export interface CoTenancyUnitPayload {
  block: string;
  label: string;
  bedroom_count: number;
  shared_kitchen_description?: string | null;
  shared_liability_note?: string | null;
}

export function useCreateCoTenancyUnitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CoTenancyUnitPayload) => {
      const { data } = await api.post<{ data: CoTenancyUnitItem }>("/api/admin/co-tenancy-units", payload);
      return data.data;
    },
    onSuccess: () => invalidateCoTenancyUnits(qc),
  });
}

export function useUpdateCoTenancyUnitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CoTenancyUnitPayload> & { id: string }) => {
      const { data } = await api.patch<{ data: CoTenancyUnitItem }>(`/api/admin/co-tenancy-units/${id}`, payload);
      return data.data;
    },
    onSuccess: () => invalidateCoTenancyUnits(qc),
  });
}

export function useDeleteCoTenancyUnitMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/co-tenancy-units/${id}`);
    },
    onSuccess: () => invalidateCoTenancyUnits(qc),
  });
}


export function useTerminateTenancyMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TerminateTenancyPayload) => {
      if (!id) throw new Error("Tenancy id required");
      const { data } = await api.post<SingleResource<Tenancy>>(
        `/api/admin/tenancies/${id}/terminate`,
        payload,
      );
      return data.data;
    },
    onSuccess: (tenancy) => {
      qc.invalidateQueries({ queryKey: ["admin", "tenancies"] });
      qc.invalidateQueries({ queryKey: ["admin", "tenancies", tenancy.id] });
      qc.invalidateQueries({ queryKey: ["admin", "flats"] });
      qc.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });
}

// ─── Payments ───────────────────────────────────────────────────────────────

export function useRecordPaymentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: StorePaymentPayload) => {
      const { data } = await api.post<SingleResource<Payment>>("/api/admin/payments", payload);
      return data.data;
    },
    onSuccess: (payment) => {
      qc.invalidateQueries({ queryKey: ["admin", "payments"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices", payment.invoice_id] });
      qc.invalidateQueries({ queryKey: ["admin", "tenancies", payment.tenancy_id] });
      qc.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });
}

export function useRaiseInvoiceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: StoreInvoicePayload) => {
      const { data } = await api.post<SingleResource<Invoice>>("/api/admin/invoices", payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
  });
}

export function useReversePaymentMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ReversePaymentPayload) => {
      if (!id) throw new Error("Payment id required");
      const { data } = await api.post<SingleResource<Payment>>(
        `/api/admin/payments/${id}/reverse`,
        payload,
      );
      return data.data;
    },
    onSuccess: (payment) => {
      qc.invalidateQueries({ queryKey: ["admin", "payments"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      qc.invalidateQueries({ queryKey: ["admin", "invoices", payment.invoice_id] });
      qc.invalidateQueries({ queryKey: ["admin", "tenancies", payment.tenancy_id] });
      qc.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });
}

/**
 * Receipt PDF download. Fetched via the authenticated axios instance as a
 * blob — a plain <a href> top-level navigation drops the Sanctum session
 * (no Origin/XHR header → treated as a stateless request → 401), so the PDF
 * must be pulled through the same client that carries the session cookie.
 */
async function downloadPdf(path: string, filename: string): Promise<void> {
  const res = await api.get(path, { responseType: "blob" });
  const blob = new Blob([res.data as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before reclaiming memory.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function downloadInvoiceReceipt(invoiceId: string, invoiceNumber?: string): Promise<void> {
  return downloadPdf(
    `/api/admin/invoices/${invoiceId}/receipt`,
    `receipt-${invoiceNumber ?? invoiceId}.pdf`,
  );
}

export function downloadTenantStatement(tenantId: string, tenantName?: string): Promise<void> {
  return downloadPdf(
    `/api/admin/reports/tenant/${tenantId}/statement`,
    `statement-${tenantName ?? tenantId}.pdf`,
  );
}

export function downloadPaymentReceipt(paymentId: string, reference?: string): Promise<void> {
  return downloadPdf(
    `/api/admin/payments/${paymentId}/receipt`,
    `receipt-${reference ?? paymentId}.pdf`,
  );
}

// ─── Notices ────────────────────────────────────────────────────────────────

export interface NoticesQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  type?: Notice["type"];
  category?: string;
  status?: Notice["status"];
  sort?: string;
}

export function useNoticeCategoriesQuery() {
  return useQuery({
    queryKey: ["admin", "notices", "categories"],
    queryFn: async () => {
      const { data } = await api.get<{ data: string[] }>("/api/admin/notices/categories");
      return data.data;
    },
  });
}

export function useNoticesQuery(params: NoticesQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "notices", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.sort) q["sort"] = params.sort;
      if (params.search) q["filter[search]"] = params.search;
      if (params.type) q["filter[type]"] = params.type;
      if (params.category) q["filter[category]"] = params.category;
      if (params.status) q["filter[status]"] = params.status;
      const { data } = await api.get<Paginated<Notice>>("/api/admin/notices", { params: q });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useNoticeQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "notices", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Notice>>(`/api/admin/notices/${id}`);
      return data.data;
    },
  });
}

export function useNoticeRecipientsQuery(noticeId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ["admin", "notices", noticeId, "recipients", page],
    enabled: !!noticeId,
    queryFn: async () => {
      const { data } = await api.get<Paginated<NoticeRecipient>>(
        `/api/admin/notices/${noticeId}/recipients`,
        { params: { page, per_page: 50 } },
      );
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useCreateNoticeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: StoreNoticePayload) => {
      const { data } = await api.post<SingleResource<Notice>>("/api/admin/notices", payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "notices"] });
    },
  });
}

export function useUpdateNoticeMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateNoticePayload) => {
      if (!id) throw new Error("Notice id required");
      const { data } = await api.patch<SingleResource<Notice>>(
        `/api/admin/notices/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: (notice) => {
      qc.invalidateQueries({ queryKey: ["admin", "notices"] });
      qc.invalidateQueries({ queryKey: ["admin", "notices", notice.id] });
    },
  });
}

export function usePublishNoticeMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (publishedAt?: string) => {
      if (!id) throw new Error("Notice id required");
      const { data } = await api.post<SingleResource<Notice>>(
        `/api/admin/notices/${id}/publish`,
        publishedAt ? { published_at: publishedAt } : {},
      );
      return data.data;
    },
    onSuccess: (notice) => {
      qc.invalidateQueries({ queryKey: ["admin", "notices"] });
      qc.invalidateQueries({ queryKey: ["admin", "notices", notice.id] });
    },
  });
}

export function useDeleteNoticeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/notices/${id}`);
      return id;
    },
    onSuccess: (id) => {
      // Remove the just-deleted notice's own detail/recipients queries
      // first — invalidating ["admin","notices"] alone matches them too
      // (prefix match) and triggers a doomed refetch against a record that
      // no longer exists, surfacing as a 404 in the console right as the
      // page navigates away. Removing before invalidating means the
      // broader invalidate only has the list query left to refetch.
      qc.removeQueries({ queryKey: ["admin", "notices", id] });
      qc.invalidateQueries({ queryKey: ["admin", "notices"] });
    },
  });
}

// ─── Documents ───────────────────────────────────────────────────────────────

export interface DocumentsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  owner_id?: string;
  owner_type?: "tenant" | "flat";
  category?: string;
  visibility?: Document["visibility"];
}

export function useDocumentsQuery(params: DocumentsQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "documents", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.search) q["filter[search]"] = params.search;
      if (params.owner_id) q["filter[owner_id]"] = params.owner_id;
      if (params.owner_type) q["filter[owner_type]"] = params.owner_type;
      if (params.category) q["filter[category]"] = params.category;
      if (params.visibility) q["filter[visibility]"] = params.visibility;
      const { data } = await api.get<Paginated<Document>>("/api/admin/documents", { params: q });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useDocumentQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "documents", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Document>>(`/api/admin/documents/${id}`);
      return data.data;
    },
  });
}

export function useUploadDocumentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post<SingleResource<Document>>(
        "/api/admin/documents",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "documents"] });
    },
  });
}

export function useDeleteDocumentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/admin/documents/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "documents"] });
    },
  });
}

// ─── Rent Reviews ────────────────────────────────────────────────────────────

export interface RentReviewsQueryParams {
  page?: number;
  per_page?: number;
  tenancy_id?: string;
  status?: RentReview["status"];
}

export function useRentReviewsQuery(params: RentReviewsQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "rent-reviews", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.tenancy_id) q["filter[tenancy_id]"] = params.tenancy_id;
      if (params.status) q["filter[status]"] = params.status;
      const { data } = await api.get<Paginated<RentReview>>("/api/admin/rent-reviews", { params: q });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useRentReviewQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "rent-reviews", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<RentReview>>(`/api/admin/rent-reviews/${id}`);
      return data.data;
    },
  });
}

export function useInitiateRentReviewMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InitiateRentReviewPayload) => {
      const { data } = await api.post<SingleResource<RentReview>>("/api/admin/rent-reviews", payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rent-reviews"] });
    },
  });
}

export function useApproveRentReviewMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SingleResource<RentReview>>(`/api/admin/rent-reviews/${id}/approve`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rent-reviews"] });
    },
  });
}

export function useRejectRentReviewMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rejection_reason: string) => {
      const { data } = await api.post<SingleResource<RentReview>>(`/api/admin/rent-reviews/${id}/reject`, { rejection_reason });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rent-reviews"] });
    },
  });
}

export function usePublishRentReviewMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SingleResource<RentReview>>(`/api/admin/rent-reviews/${id}/publish`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rent-reviews"] });
    },
  });
}

export function useApplyRentReviewMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<SingleResource<RentReview>>(`/api/admin/rent-reviews/${id}/apply`);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rent-reviews"] });
      qc.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

export function useRenewTenancyMutation(tenancyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RenewTenancyPayload) => {
      const { data } = await api.post<SingleResource<Tenancy> & { bill_push: BillPushResult | null }>(
        `/api/admin/tenancies/${tenancyId}/renew`,
        payload,
      );
      return { tenancy: data.data, billPush: data.bill_push };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

// ─── Users ───────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: "super_admin" | "estate_manager";
  status: "active" | "suspended" | "invited";
  last_login_at: string | null;
  password_changed_at: string | null;
  email_verified_at: string | null;
  created_at: string;
}

export interface InviteUserPayload {
  full_name: string;
  email: string;
  phone?: string;
  role: "super_admin" | "estate_manager";
}

export interface UpdateUserPayload {
  full_name?: string;
  phone?: string;
  role?: "super_admin" | "estate_manager";
  status?: "active" | "suspended";
}

export interface UsersQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  role?: string;
  status?: string;
}

export function useUsersQuery(params: UsersQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "users", params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<AdminUser>>("/api/admin/users", { params });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useInviteUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InviteUserPayload) => {
      const { data } = await api.post<{ data: AdminUser; temp_password: string }>("/api/admin/users", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useUpdateUserMutation(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateUserPayload) => {
      const { data } = await api.patch<SingleResource<AdminUser>>(`/api/admin/users/${userId}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useResetUserPasswordMutation(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ temp_password: string }>(`/api/admin/users/${userId}/reset-password`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useSendVerificationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<{ message: string }>(`/api/admin/users/${userId}/send-verification`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useDeactivateUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/api/admin/users/${userId}`);
      return userId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

// ─── Audit Log ───────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: number;
  log_name: string | null;
  description: string;
  event: string | null;
  subject_type: string | null;
  subject_id: string | null;
  subject_label: string | null;
  causer: { id: string; full_name: string; email: string } | null;
  attributes: Record<string, unknown> | null;
  old: Record<string, unknown> | null;
  extra: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  log_name?: string;
  from?: string;
  to?: string;
}

export function useAuditQuery(params: AuditQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "audit", params],
    queryFn: async () => {
      const { data } = await api.get<Paginated<AuditEntry>>("/api/admin/audit", { params });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useAuditLogNamesQuery() {
  return useQuery({
    queryKey: ["admin", "audit", "log-names"],
    queryFn: async () => {
      const { data } = await api.get<{ data: string[] }>("/api/admin/audit/log-names");
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface SettingsData {
  estate: {
    name?: string | null;
    address?: string | null;
    support_phone?: string | null;
    support_email?: string | null;
  };
  billing: {
    service_charge_enabled?: boolean | null;
    default_invoice_due_days?: number | null;
    late_fee_kobo?: number | null;
    default_rent_disbursement_id?: string | null;
  };
  notifications: {
    lease_expiry_reminder_days?: number[] | null;
    service_charge_reminder_days?: number[] | null;
  };
  security: {
    password_min_length?: number | null;
    session_idle_minutes?: number | null;
  };
}

export type SettingsPayload = {
  "estate.name"?: string;
  "estate.address"?: string;
  "estate.support_phone"?: string;
  "estate.support_email"?: string;
  "billing.service_charge_enabled"?: boolean;
  "billing.default_invoice_due_days"?: number;
  "billing.late_fee_kobo"?: number;
  "billing.default_rent_disbursement_id"?: string;
  "notifications.lease_expiry_reminder_days"?: number[];
  "notifications.service_charge_reminder_days"?: number[];
  "security.password_min_length"?: number;
  "security.session_idle_minutes"?: number;
};

export function useSettingsQuery() {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: async () => {
      const { data } = await api.get<{ data: SettingsData }>("/api/admin/settings");
      return data.data;
    },
    staleTime: Infinity,
  });
}

export function useUpdateSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SettingsPayload) => {
      const { data } = await api.patch<{ data: SettingsData }>("/api/admin/settings", payload);
      return data.data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(["admin", "settings"], updated);
    },
  });
}

// ─── Reports ─────────────────────────────────────────────────────────────────

export interface ReportsSummary {
  occupancy: { total_flats: number; occupied: number; vacant: number; rate: number };
  revenue: { total_billed_kobo: number; total_collected_kobo: number; current_month_kobo: number; collection_rate: number };
  arrears: { overdue_invoices: number; overdue_balance_kobo: number };
  expiry: { expiring_in_30_days: number; expiring_in_90_days: number };
}

export function useReportsSummaryQuery() {
  return useQuery({
    queryKey: ["admin", "reports", "summary"],
    queryFn: async () => {
      const { data } = await api.get<ReportsSummary>("/api/admin/reports/summary");
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export async function exportReport(
  type: "arrears" | "revenue" | "occupancy" | "expiry" | "flat-breakdown",
): Promise<void> {
  const res = await api.get(`/api/admin/reports/${type}/export`, { responseType: "blob" });
  const blob = new Blob([res.data as BlobPart], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${type}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Separate from exportReport() above — this is the one report that's
// period-scoped, so it needs a month range rather than firing with no params.
export async function exportFlatBreakdownReport(range: {
  month: number;
  year: number;
  endMonth: number;
  endYear: number;
}): Promise<void> {
  const res = await api.get("/api/admin/reports/flat-breakdown/export", {
    responseType: "blob",
    params: {
      month: range.month,
      year: range.year,
      end_month: range.endMonth,
      end_year: range.endYear,
    },
  });
  const blob = new Blob([res.data as BlobPart], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `flat-breakdown-${range.year}-${String(range.month).padStart(2, "0")}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadDocument(documentId: string, filename?: string): Promise<void> {
  const res = await api.get(`/api/admin/documents/${documentId}/download`, { responseType: "blob" });
  const blob = new Blob([res.data as BlobPart], { type: res.headers["content-type"] as string });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ─── Meters ─────────────────────────────────────────────────────────────────

export interface Meter {
  id: string;
  meter_number: string;
  type: "prepaid" | "postpaid";
  status: "active" | "suspended" | "disconnected";
  diesel_deposit_kobo: number;
  diesel_deposit_formatted: string;
  notes: string | null;
  last_vending_at: string | null;
  last_vending_kwh: number | null;
  flat: { id: string; code: string; block: string } | null;
  transactions?: Array<{
    id: string;
    amount_formatted: string;
    kwh_purchased: number;
    token_number: string | null;
    status: string;
    created_at: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface MetersQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: Meter["status"];
  flat_id?: string;
}

export interface StoreMeterPayload {
  flat_id: string;
  meter_number: string;
  type?: Meter["type"];
  status?: Meter["status"];
  diesel_deposit_kobo?: number;
  notes?: string;
}

export function useMetersQuery(params: MetersQueryParams = {}) {
  return useQuery({
    queryKey: ["admin", "meters", params],
    queryFn: async () => {
      const q: Record<string, string | number> = {};
      if (params.page) q["page"] = params.page;
      if (params.per_page) q["per_page"] = params.per_page;
      if (params.search) q["filter[search]"] = params.search;
      if (params.status) q["filter[status]"] = params.status;
      if (params.flat_id) q["filter[flat_id]"] = params.flat_id;
      const { data } = await api.get<Paginated<Meter>>("/api/admin/meters", { params: q });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useMeterQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "meters", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<SingleResource<Meter>>(`/api/admin/meters/${id}`);
      return data.data;
    },
  });
}

export function useCreateMeterMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: StoreMeterPayload) => {
      const { data } = await api.post<SingleResource<Meter>>("/api/admin/meters", payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "meters"] }),
  });
}

export function useUpdateMeterMutation(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<StoreMeterPayload>) => {
      if (!id) throw new Error("Meter id required");
      const { data } = await api.patch<SingleResource<Meter>>(`/api/admin/meters/${id}`, payload);
      return data.data;
    },
    onSuccess: (meter) => {
      qc.invalidateQueries({ queryKey: ["admin", "meters"] });
      qc.setQueryData(["admin", "meters", meter.id], meter);
    },
  });
}

export function useDeleteMeterMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (meterId: string) => {
      await api.delete(`/api/admin/meters/${meterId}`);
      return meterId;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "meters"] }),
  });
}

export function useMeterTariffQuery() {
  return useQuery({
    queryKey: ["admin", "meters", "tariff"],
    queryFn: async () => {
      const { data } = await api.get<{ data: { rate_per_kwh_kobo: number; vat_percent: number } }>(
        "/api/admin/meters/tariff/rates"
      );
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Tenant Approval (Phase 8) ──────────────────────────────────────────────

export interface TenantForApproval extends Tenant {
  approval_status: "pending" | "approved" | "rejected";
  has_lease_agreement?: boolean;
  lease_agreement_complete?: boolean;
  account_status?: "active" | "suspended" | "invited";
  flat?: { id: string; code: string } | null;
}

export interface TenantApprovalStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export function useTenantApprovalQuery(
  params: {
    page?: number;
    per_page?: number;
    search?: string;
    approval_status?: "pending" | "approved" | "rejected";
  } = {},
) {
  const { page = 1, per_page = 25, search, approval_status } = params;
  return useQuery({
    queryKey: ["admin", "tenants", "approval", page, per_page, search, approval_status],
    queryFn: async () => {
      const { data } = await api.get<Paginated<TenantForApproval> & { stats: TenantApprovalStats }>(
        "/api/admin/tenants/approval",
        {
          params: {
            page,
            per_page,
            "filter[search]": search || undefined,
            "filter[approval_status]": approval_status || undefined,
          },
        },
      );
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export async function exportTenantApprovals(params: {
  search?: string;
  approval_status?: "pending" | "approved" | "rejected";
} = {}): Promise<void> {
  const res = await api.get("/api/admin/tenants/approval/export", {
    responseType: "blob",
    params: {
      "filter[search]": params.search || undefined,
      "filter[approval_status]": params.approval_status || undefined,
    },
  });
  const blob = new Blob([res.data as BlobPart], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `tenant-approvals-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface BillPushResult {
  status: "pushed" | "skipped_existing" | "skipped_no_group" | "skipped_no_disbursement" | "skipped_no_rent_amount" | "failed";
  message: string;
}

// Present only when uploading/editing a current lease agreement corrected
// (or first ever set) an already-approved tenant's active Tenancy rent.
export interface TenancyRentSync {
  from_kobo: number;
  to_kobo: number;
}

export function useApproveTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<SingleResource<TenantForApproval> & { bill_push: BillPushResult | null }>(
        `/api/admin/tenants/${tenantId}/approve`,
      );
      return { tenant: data.data, billPush: data.bill_push };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      // Approval now auto-creates a Tenancy and flips the flat to occupied.
      queryClient.invalidateQueries({ queryKey: ["admin", "flats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

// For a resident IOBOTech already links to a flat that already has an
// approved leaseholder (e.g. one resident invited another). No lease
// agreement required, no Tenancy created — just a portal login for
// vending/payments, riding on the flat's existing active tenancy.
export function useApproveOccupantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<SingleResource<TenantForApproval>>(
        `/api/admin/tenants/${tenantId}/approve-occupant`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

// Backfill a Tenancy for a tenant who was approved before this became
// automatic (has a login already, but no Tenancy — flat still shows vacant).
export function useCreateTenancyForApprovedTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<SingleResource<TenantForApproval> & { bill_push: BillPushResult | null }>(
        `/api/admin/tenants/${tenantId}/create-tenancy`,
      );
      return { tenant: data.data, billPush: data.bill_push };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "flats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

// Starts a time-boxed, read-only "View as Tenant" session. On success the
// caller redirects the browser to the tenant portal — the shared session
// cookie (SESSION_DOMAIN=.lekkiservicedflats.com) carries over, no re-auth.
export function useImpersonateTenantMutation() {
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<{ data: { expires_at: string } }>(
        `/api/admin/tenants/${tenantId}/impersonate`,
      );
      return data.data;
    },
  });
}

export function useRejectTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, reason }: { tenantId: string; reason: string }) => {
      const { data } = await api.post<SingleResource<TenantForApproval>>(`/api/admin/tenants/${tenantId}/reject`, {
        rejection_reason: reason,
      });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export function useRevokeTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<SingleResource<TenantForApproval>>(`/api/admin/tenants/${tenantId}/revoke`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export function useReinstateTenantMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<SingleResource<TenantForApproval>>(`/api/admin/tenants/${tenantId}/reinstate`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export function useResendTenantCredentialsMutation() {
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const { data } = await api.post<{ message: string }>(`/api/admin/tenants/${tenantId}/resend-credentials`);
      return data;
    },
  });
}

export function useSyncIOBOTechTenantsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ message: string; data: { synced: number; created: number } }>(
        "/api/admin/tenants/sync-iobotech",
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });
}

export interface TenancyDocument {
  id: string;
  tenant_id: string;
  document_type: "lease_agreement" | "id_verification" | "other";
  file_name: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  superseded_at: string | null;
  created_at: string;
  starts_on: string | null;
  ends_on: string | null;
  rent_billing_cycle: "yearly" | "monthly" | null;
  rent_amount_kobo: number | null;
}

export function useTenancyDocumentsQuery(tenantId: string | null) {
  return useQuery({
    queryKey: ["admin", "tenants", tenantId, "documents"],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data } = await api.get<{ data: TenancyDocument[] }>(`/api/admin/tenants/${tenantId}/documents`);
      return data.data;
    },
  });
}

export function useUploadTenancyDocumentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      file,
      documentType,
      overwrite,
      startsOn,
      endsOn,
      isOpenEnded,
      rentBillingCycle,
      rentAmountKobo,
    }: {
      tenantId: string;
      file: File;
      documentType: string;
      overwrite?: boolean;
      startsOn?: string;
      endsOn?: string;
      isOpenEnded?: boolean;
      rentBillingCycle?: "yearly" | "monthly";
      rentAmountKobo?: number;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", documentType);
      if (overwrite) formData.append("overwrite", "1");
      if (startsOn) formData.append("starts_on", startsOn);
      if (endsOn) formData.append("ends_on", endsOn);
      if (isOpenEnded) formData.append("is_open_ended", "1");
      if (rentBillingCycle) formData.append("rent_billing_cycle", rentBillingCycle);
      if (rentAmountKobo) formData.append("rent_amount_kobo", String(rentAmountKobo));
      const { data } = await api.post<{
        message: string;
        data: TenancyDocument;
        tenancy_rent_sync: TenancyRentSync | null;
      }>(`/api/admin/tenants/${tenantId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", variables.tenantId, "documents"] });
      // A lease upload can now correct an already-active tenancy's rent.
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

export function useUpdateLeaseDatesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      documentId,
      startsOn,
      endsOn,
      isOpenEnded,
      rentBillingCycle,
      rentAmountKobo,
    }: {
      tenantId: string;
      documentId: string;
      startsOn: string;
      endsOn: string | null;
      isOpenEnded?: boolean;
      rentBillingCycle: "yearly" | "monthly";
      rentAmountKobo: number;
    }) => {
      const { data } = await api.patch<{
        message: string;
        data: TenancyDocument;
        tenancy_rent_sync: TenancyRentSync | null;
      }>(
        `/api/admin/tenants/${tenantId}/documents/${documentId}/lease-dates`,
        {
          starts_on: startsOn,
          ends_on: endsOn,
          is_open_ended: isOpenEnded ?? false,
          rent_billing_cycle: rentBillingCycle,
          rent_amount_kobo: rentAmountKobo,
        },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", "approval"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", variables.tenantId, "documents"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenancies"] });
    },
  });
}

export function useDownloadTenancyDocument() {
  return async (tenantId: string, document: TenancyDocument) => {
    const { data } = await api.get(`/api/admin/tenants/${tenantId}/documents/${document.id}/download`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(data as Blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = document.file_name;
    a.click();
    URL.revokeObjectURL(url);
  };
}

// ─── IOBOTech Billing ────────────────────────────────────────────────────────

export interface IOBOTechInvoice {
  invoiceId: string;
  unit: string;
  amount: number; // plain Naira
  amountToPay: number;
  isPaid: boolean;
  status: string;
  dueDate: string | null;
  paidOn?: string | null;
  paidBy?: { firstName: string; lastName: string } | null;
  deactivationReason?: string | null;
}

export interface IOBOTechBill {
  billingId: string;
  name: string;
  description: string;
  group: string;
  bank: string;
  amount: number; // plain Naira
  status: string;
  priority: string;
  createdBy: string;
  invoices?: IOBOTechInvoice[];
}

export interface IOBOTechBillsResult {
  bills: IOBOTechBill[];
  last_refreshed_at: string | null;
}

export function useIOBOTechBillsQuery(params: { flat_id?: string } = {}) {
  return useQuery({
    queryKey: ["admin", "iobotech-billing", params],
    queryFn: async (): Promise<IOBOTechBillsResult> => {
      const { data } = await api.get<{ success: boolean; data: IOBOTechBill[]; last_refreshed_at: string | null }>(
        "/api/admin/billing",
        { params },
      );
      return { bills: data.data ?? [], last_refreshed_at: data.last_refreshed_at ?? null };
    },
    placeholderData: (previous) => previous,
  });
}

// Forces an immediate, synchronous re-pull of every IOBOTech bill+invoice —
// can take up to ~60s since IOBOTech's API is occasionally slow.
export function useRefreshIOBOTechBillsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/admin/billing/refresh");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "iobotech-billing"] });
    },
  });
}

export interface IOBOTechDisbursement {
  disbursementId: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
}

export function useIOBOTechDisbursementsQuery() {
  return useQuery({
    queryKey: ["admin", "iobotech-disbursements"],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: IOBOTechDisbursement[] }>(
        "/api/admin/billing/disbursements",
      );
      return data.data ?? [];
    },
    staleTime: 5 * 60 * 1000, // disbursements rarely change
  });
}

export interface FlatNameSyncNewUnit {
  group_id: string;
  name: string;
  block: string;
  unit: string;
  code: string;
  total_members: number | null;
}

export interface FlatNameSyncResult {
  applied: boolean;
  groups: number;
  changes: { code: string; from: string; to: string; new_code: string }[];
  linked: string[];
  unmatched: string[];
  conflicts: { code: string; new_code: string }[];
  ambiguous: { code: string; group_name: string }[];
  // Billing groups IOBOTech knows about with no matching flat at all —
  // candidate NEW apartments (e.g. a co-tenancy room whose meter was just
  // set up, no resident assigned yet). Never auto-created.
  new_units: FlatNameSyncNewUnit[];
  new_units_ambiguous: { group_id: string; name: string }[];
  new_units_conflicts: { group_id: string; name: string; code: string }[];
  // Only meaningful on the POST (apply) response — flats actually created
  // this run (can be a subset of what was selected, if a submitted group
  // id went stale between preview and click).
  created: { id: string; code: string }[];
}

export function useFlatNameSyncPreview(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "flats", "sync-names", "preview"],
    queryFn: async () => {
      const { data } = await api.get<FlatNameSyncResult>("/api/admin/flats/sync-names");
      return data;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useApplyFlatNameSyncMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (createGroupIds: string[] = []) => {
      const { data } = await api.post<FlatNameSyncResult>("/api/admin/flats/sync-names", {
        create_group_ids: createGroupIds,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "flats"] }),
  });
}

export interface DuplicateFlatTenant {
  id: string;
  full_name: string;
  approval_status: "pending" | "approved" | "rejected" | null;
}

export interface DuplicateFlatPair {
  stale_flat: { id: string; code: string };
  stale_tenants: DuplicateFlatTenant[];
  correct_flat: { id: string; code: string };
  correct_tenants: DuplicateFlatTenant[];
  group_name: string;
  blockers: string[];
  safe_to_delete: boolean;
}

export interface DuplicateFlatGroupConflict {
  group_name: string;
  flats: {
    flat: { id: string; code: string };
    tenants: DuplicateFlatTenant[];
    blockers: string[];
  }[];
}

export interface DuplicateFlatsResult {
  duplicates: DuplicateFlatPair[];
  fixable: { flat: { id: string; code: string; unit: string; block: string }; group_name: string }[];
  ambiguous: { flat: { id: string; code: string }; candidates: string[] }[];
  group_conflicts: DuplicateFlatGroupConflict[];
}

export function useDuplicateFlatsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "flats", "duplicates"],
    queryFn: async () => {
      const { data } = await api.get<DuplicateFlatsResult>("/api/admin/flats/duplicates");
      return data;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDeleteDuplicateFlatMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (flatId: string) => {
      const { data } = await api.delete<{ message: string }>(`/api/admin/flats/${flatId}/duplicate`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "flats"] });
    },
  });
}

export interface IOBOTechGroup {
  name: string;
  groupId: string;
  totalMembers: number;
  description?: string;
}

export function useIOBOTechGroupsQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "iobotech-groups"],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: IOBOTechGroup[] }>(
        "/api/admin/billing/groups",
      );
      return data.data ?? [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export interface PushBillPayload {
  disbursement_id: string;
  name: string;
  description?: string;
  cycle: "annual" | "monthly";
  target: "all" | "apartment" | "group";
  bedrooms?: number;       // required when target=apartment
  group_id?: string;       // required when target=group
  amount_kobo: number;
  day_of_month: number;
  month_in_year?: number;  // required when cycle=annual
  start_immediately: boolean;
  sync_as_rent_default?: boolean; // target=apartment only: also sets Flat.base_rent_kobo for this bedroom count
}

export function usePushBillMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PushBillPayload) => {
      const { data } = await api.post("/api/admin/billing/push", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "iobotech-billing"] });
      qc.invalidateQueries({ queryKey: ["admin", "flats"] });
    },
  });
}

// ─── Service Charge Deposit Utilisation Report ──────────────────────────────

export interface ServiceChargeCategory {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

export function useServiceChargeCategoriesQuery() {
  return useQuery({
    queryKey: ["admin", "service-charge", "categories"],
    queryFn: async () => {
      const { data } = await api.get<{ data: ServiceChargeCategory[] }>(
        "/api/admin/service-charge/categories",
      );
      return data.data;
    },
  });
}

export function useCreateServiceChargeCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; sort_order?: number }) => {
      const { data } = await api.post<{ data: ServiceChargeCategory }>(
        "/api/admin/service-charge/categories",
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "service-charge", "categories"] });
    },
  });
}

export function useUpdateServiceChargeCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: { id: string; name?: string; sort_order?: number; is_active?: boolean }) => {
      const { data } = await api.patch<{ data: ServiceChargeCategory }>(
        `/api/admin/service-charge/categories/${id}`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "service-charge", "categories"] });
    },
  });
}

export interface ServiceChargeUpload {
  id: string;
  statement_period: string;
  file_name: string;
  superseded_at: string | null;
  deposit_entries_count: number;
  uploaded_by: { id: string; full_name: string } | null;
  created_at: string;
}

export function useServiceChargeUploadsQuery() {
  return useQuery({
    queryKey: ["admin", "service-charge", "uploads"],
    queryFn: async () => {
      const { data } = await api.get<{ data: ServiceChargeUpload[] }>(
        "/api/admin/service-charge/uploads",
      );
      return data.data;
    },
  });
}

export interface ServiceChargeUploadRow {
  tenancy_id: string;
  flat: string | null;
  tenant: string | null;
  additional_deposits_kobo: number;
  utilisation_total_kobo: number;
  categories: { name: string | null; current_month_kobo: number }[];
}

export function useServiceChargeUploadDetailQuery(uploadId: string | null) {
  return useQuery({
    queryKey: ["admin", "service-charge", "uploads", uploadId],
    queryFn: async () => {
      const { data } = await api.get<{ data: { upload: ServiceChargeUpload; rows: ServiceChargeUploadRow[] } }>(
        `/api/admin/service-charge/uploads/${uploadId}`,
      );
      return data.data;
    },
    enabled: !!uploadId,
  });
}

export async function downloadServiceChargeTemplate(): Promise<void> {
  const res = await api.get("/api/admin/service-charge/template", { responseType: "blob" });
  const blob = new Blob([res.data as BlobPart], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `service-charge-template-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useUploadServiceChargeStatementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ statementPeriod, file }: { statementPeriod: string; file: File }) => {
      const formData = new FormData();
      formData.append("statement_period", statementPeriod);
      formData.append("file", file);
      const { data } = await api.post<{ data: ServiceChargeUpload }>(
        "/api/admin/service-charge/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "service-charge", "uploads"] });
    },
  });
}

// ─── KYC Review (rental booking module, Phase 3) ───────────────────────────

export interface KycQueueItem {
  id: string;
  status: string;
  verification_path: "standard" | "accelerated";
  submitted_at: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  reviewer_name: string | null;
  applicant_name: string;
  unit_code: string;
  government_id_type: string | null;
  portal_check_result: string | null;
}

export interface KycDocumentItem {
  id: string;
  doc_type: string;
  uploaded_at: string;
}

export interface KycAuditLogItem {
  action: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface KycApplicationDetail {
  id: string;
  status: string;
  verification_path: "standard" | "accelerated";
  applicant_profile: Record<string, unknown>;
  applicant_name: string;
  applicant_email: string;
  unit_code: string;
  decided_at: string | null;
  decision_reason: string | null;
  reviewer_name: string | null;
  identity_verification: {
    government_id_type: string;
    government_id_number_masked: string;
    date_of_birth: string | null;
    gender: string;
    nationality: string;
    verification_portal: string | null;
    portal_check_result: string | null;
    portal_checked_at: string | null;
    reviewer_notes: string | null;
  } | null;
  documents: KycDocumentItem[];
  audit_log: KycAuditLogItem[];
}

export function useKycQueueQuery(tab: "pending" | "completed" = "pending") {
  return useQuery({
    queryKey: ["admin", "kyc", "queue", tab],
    queryFn: async () => {
      const { data } = await api.get<{ data: KycQueueItem[] }>("/api/admin/kyc/queue", { params: { tab } });
      return data.data;
    },
  });
}

export function useKycApplicationQuery(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "kyc", "applications", id],
    queryFn: async () => {
      const { data } = await api.get<KycApplicationDetail>(`/api/admin/kyc/applications/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

function invalidateKyc(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ["admin", "kyc", "queue"] });
  qc.invalidateQueries({ queryKey: ["admin", "kyc", "applications", id] });
}

export function useStoreKycVerificationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, verificationPortal, portalCheckResult, reviewerNotes,
    }: {
      id: string;
      verificationPortal: string;
      portalCheckResult: string;
      reviewerNotes?: string;
    }) => {
      const { data } = await api.post<{ status: string }>(`/api/admin/kyc/applications/${id}/verification`, {
        verification_portal: verificationPortal,
        portal_check_result: portalCheckResult,
        reviewer_notes: reviewerNotes || undefined,
      });
      return data;
    },
    onSuccess: (_data, vars) => invalidateKyc(qc, vars.id),
  });
}

export function useDecideKycMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, decision, reason,
    }: {
      id: string;
      decision: "approve" | "query" | "reject";
      reason?: string;
    }) => {
      const { data } = await api.post<{ status: string }>(`/api/admin/kyc/applications/${id}/decision`, {
        decision,
        reason: reason || undefined,
      });
      return data;
    },
    onSuccess: (_data, vars) => invalidateKyc(qc, vars.id),
  });
}

// ─── Boarding (rental booking module Phase 6) ──────────────────────────────

export interface BoardingQueueItem {
  id: string;
  applicant_name: string;
  applicant_email: string;
  unit_code: string;
  key_profiling_status: "pending" | "confirmed";
  key_profiling_confirmed_at: string | null;
  boarding_instructions_sent_at: string | null;
}

export function useBoardingQueueQuery(tab: "pending" | "completed" = "pending") {
  return useQuery({
    queryKey: ["admin", "boarding", "queue", tab],
    queryFn: async () => {
      const { data } = await api.get<{ data: BoardingQueueItem[] }>("/api/admin/boarding/queue", { params: { tab } });
      return data.data;
    },
  });
}

export function useConfirmKeyProfilingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ status: string }>(`/api/admin/boarding/${id}/confirm-key-profiling`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "boarding", "queue"] }),
  });
}

// ─── Inspections & security deposit (rental booking module Phase 7) ───────

export interface InspectionQueueItem {
  id: string;
  type: "preliminary" | "post_surrender";
  status: "due" | "completed" | "cancelled";
  unit_code: string;
  tenant_name: string;
  tenancy_ends_on: string | null;
  findings: string | null;
  fault_notes: string | null;
  invoice_estimate_kobo: number | null;
  conducted_at: string | null;
  inspector_name: string | null;
  deposit_resolution: "released" | "deducted" | null;
  deposit_kobo: number;
  tenancy_id: string;
  photos: Array<{ id: string; thumb_url: string; gallery_url: string }>;
}

export function useInspectionsQueueQuery(tab: "due" | "completed" = "due") {
  return useQuery({
    queryKey: ["admin", "inspections", "queue", tab],
    queryFn: async () => {
      const { data } = await api.get<{ data: InspectionQueueItem[] }>("/api/admin/inspections/queue", { params: { tab } });
      return data.data;
    },
  });
}

export function useLogInspectionReportMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id, findings, faultNotes, invoiceEstimateKobo, photos,
    }: {
      id: string;
      findings: string;
      faultNotes?: string;
      invoiceEstimateKobo?: number;
      photos?: File[];
    }) => {
      const form = new FormData();
      form.append("findings", findings);
      if (faultNotes) form.append("fault_notes", faultNotes);
      if (invoiceEstimateKobo) form.append("invoice_estimate_kobo", String(invoiceEstimateKobo));
      (photos ?? []).forEach((file) => form.append("photos[]", file));

      const { data } = await api.post(`/api/admin/inspections/${id}/report`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "inspections", "queue"] }),
  });
}

export function useResolveDepositMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenancyId, resolution, deductionKobo, notes,
    }: {
      tenancyId: string;
      resolution: "released" | "deducted";
      deductionKobo?: number;
      notes?: string;
    }) => {
      const { data } = await api.post(`/api/admin/tenancies/${tenancyId}/resolve-deposit`, {
        resolution,
        deduction_kobo: deductionKobo || undefined,
        notes: notes || undefined,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "inspections", "queue"] }),
  });
}

export function useDownloadKycDocument() {
  return async (kycApplicationId: string, document: KycDocumentItem) => {
    const { data } = await api.get(
      `/api/admin/kyc/applications/${kycApplicationId}/documents/${document.id}/download`,
      { responseType: "blob" },
    );
    const url = URL.createObjectURL(data as Blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = `${document.doc_type}-${document.id}`;
    a.click();
    URL.revokeObjectURL(url);
  };
}

// ─── Unconfirmed recharges (a recharge timed out at IOBOTech, outcome unknown) ──

export interface UnconfirmedRechargeItem {
  id: string;
  status: "unconfirmed" | "confirmed" | "dismissed";
  amount_kobo: number;
  amount_formatted: string;
  attempted_at: string;
  meter_number: string | null;
  tenant: { id: string; full_name: string } | null;
  flat_code: string | null;
  /** Cut off by Take-or-Pay right now, so confirming also switches their meter back on. */
  tenant_cut_off: boolean;
  hold_active: boolean;
  auto_check_note: string | null;
  emailed_ict: boolean;
  resolution_method: "auto_meter_balance" | "manual" | null;
  iobotech_reference: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
}

export function useUnconfirmedRechargesQuery(tab: "pending" | "history" = "pending") {
  return useQuery({
    queryKey: ["admin", "recharges", "unconfirmed", tab],
    queryFn: async () => {
      const { data } = await api.get<{ data: UnconfirmedRechargeItem[] }>("/api/admin/recharges/unconfirmed", { params: { tab } });
      return data.data;
    },
  });
}

export function useConfirmUnconfirmedRechargeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; iobotech_reference: string; note?: string; verified_on_iobotech: boolean }) => {
      const { data } = await api.post<{ data: { id: string; status: string; receipt_number: string; power_restored: boolean | null } }>(
        `/api/admin/recharges/unconfirmed/${id}/confirm`,
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "recharges", "unconfirmed"] });
      qc.invalidateQueries({ queryKey: ["admin", "notifications"] });
    },
  });
}

export function useDismissUnconfirmedRechargeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      const { data } = await api.post<{ data: { id: string; status: string } }>(`/api/admin/recharges/unconfirmed/${id}/dismiss`, { note });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "recharges", "unconfirmed"] });
      qc.invalidateQueries({ queryKey: ["admin", "notifications"] });
    },
  });
}
